/**
 * /posvenda/satisfacao — o que os clientes responderam (A27).
 *
 * O NPS já era exibido no Cliente 360 e nunca teve o que exibir: nada no
 * sistema escrevia satisfação. Esta é a tela do outro lado do link —
 * onde a equipe lê as respostas, registra a nota que veio por telefone e
 * reenvia o link de quem ainda não respondeu.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiLink, FiPlus, FiRefreshCw } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import ClienteSelect from "../../../components/ClienteSelect";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  copiarLinkSatisfacao,
  FAIXA_NPS,
  listarSatisfacoes,
  obterResumoNps,
  registrarSatisfacao,
} from "../../../services/satisfacao";
import { PageContainer } from "../../../styles/shared";
import { formatarDataLocal } from "../../../utils/dinheiro";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  InfoMsg,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";

const FILTROS = [
  { valor: "", label: "Todas" },
  { valor: "true", label: "Respondidas" },
  { valor: "false", label: "Aguardando resposta" },
];

export default function SatisfacaoLista() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();

  const [itens, setItens] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [registrando, setRegistrando] = useState(false);
  // ClienteSelect devolve o OBJETO do cliente, nao o id.
  const [novo, setNovo] = useState({ cliente: null, nota: "", comentario: "" });

  const podeRegistrar = isMaster || hasAllPermissions(["satisfacao:registrar"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lista, res] = await Promise.all([
        listarSatisfacoes(request, {
          respondidas: filtro === "" ? undefined : filtro,
        }),
        obterResumoNps(request, {}, { silentError: true }).catch(() => null),
      ]);
      setItens(Array.isArray(lista) ? lista : lista?.results || []);
      setResumo(res);
    } catch (err) {
      setError(err?.message || "Erro ao carregar as pesquisas.");
      setItens([]);
    } finally {
      setLoading(false);
    }
  }, [request, filtro]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => {
    const semResposta = resumo?.pendentes ?? 0;
    const nps = resumo?.nps;
    return [
      {
        label: "NPS",
        // Sem resposta nenhuma o NPS é ausência de dado, não zero — zero
        // seria o pior resultado possível.
        value: nps == null ? "—" : nps,
        // O rotulo diz a regua: sem filtro de periodo, este NPS e de
        // todas as respostas ja recebidas.
        sub: nps == null
          ? "sem respostas ainda"
          : "promotores − detratores, todas as respostas",
        tone: nps == null ? "neutral" : nps >= 50 ? "success" : nps >= 0 ? "warning" : "danger",
      },
      { label: "Respostas", value: resumo?.total_respostas ?? 0, tone: "neutral" },
      {
        label: "Promotores",
        value: resumo?.promotores ?? 0,
        sub: "nota 9 ou 10",
        tone: "success",
      },
      {
        label: "Detratores",
        value: resumo?.detratores ?? 0,
        sub: "nota até 6",
        tone: (resumo?.detratores ?? 0) > 0 ? "danger" : "neutral",
      },
      {
        label: "Aguardando",
        value: semResposta,
        sub: semResposta > 0 ? "link enviado, sem resposta" : "",
        tone: "neutral",
      },
    ];
  }, [resumo]);

  const pedirLink = async (item) => {
    const { url } = await copiarLinkSatisfacao(request, item.id);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado — pode colar no WhatsApp.");
    } catch (_) {
      // Clipboard bloqueado (http, permissão negada): mostrar a URL ainda
      // resolve, esconder seria pior.
      toast.success(url);
    }
  };

  const registrar = async () => {
    if (!novo.cliente?.id) throw new Error("Escolha o cliente.");
    if (novo.nota === "") throw new Error("Informe a nota de 0 a 10.");
    await registrarSatisfacao(request, {
      cliente: novo.cliente.id,
      nota: Number(novo.nota),
      comentario: novo.comentario,
    });
    setNovo({ cliente: null, nota: "", comentario: "" });
    setRegistrando(false);
    await carregar();
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Satisfação do cliente (NPS)"
        subtitle="A pesquisa é aberta na entrega e o cliente responde por link, sem login"
        actions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregar,
          },
          ...(podeRegistrar
            ? [{
                label: "Registrar nota",
                icon: <FiPlus size={14} aria-hidden="true" />,
                onClick: () => setRegistrando(true),
                primary: true,
              }]
            : []),
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtro</SectionTitle>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 240 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Situação</span>
          <Select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
            {FILTROS.map((f) => (
              <option key={f.valor} value={f.valor}>{f.label}</option>
            ))}
          </Select>
        </label>
      </Card>

      {error ? (
        <ErrorMsg>
          <FiAlertTriangle size={14} aria-hidden="true" /> {error}
        </ErrorMsg>
      ) : null}

      <Card>
        <SectionTitle>
          {loading ? "Carregando…" : `${itens.length} pesquisa(s)`}
        </SectionTitle>
        {!loading && itens.length === 0 ? (
          <EmptyMsg>
            Nenhuma pesquisa ainda. Elas nascem sozinhas quando um pedido é
            marcado como entregue.
          </EmptyMsg>
        ) : null}
        {itens.length > 0 ? (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Pedido</th>
                  <th>Nota</th>
                  <th>Faixa</th>
                  <th>Comentário</th>
                  <th>Respondida em</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {itens.map((s) => (
                  <tr key={s.id}>
                    <td>{s.cliente_nome}</td>
                    <td>{s.pedido_numero || "—"}</td>
                    <td style={{ fontWeight: 700 }}>
                      {s.nota == null ? "—" : s.nota}
                    </td>
                    <td>
                      <Pill $tone={FAIXA_NPS[s.classificacao] || "neutral"}>
                        {s.expirada && !s.respondida ? "Venceu" : s.classificacao}
                      </Pill>
                    </td>
                    <td
                      style={{
                        maxWidth: 320, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                      title={s.comentario || ""}
                    >
                      {s.comentario || "—"}
                    </td>
                    <td>
                      {s.respondido_em ? formatarDataLocal(s.respondido_em) : "—"}
                    </td>
                    <td>
                      {podeRegistrar && !s.respondida && !s.expirada ? (
                        <AsyncButton
                          variant="icon"
                          silentSuccess
                          errorMessage="Não foi possível gerar o link."
                          onClick={() => pedirLink(s)}
                          aria-label={`Copiar link da pesquisa de ${s.cliente_nome}`}
                          title="Copiar link para reenviar"
                        >
                          <FiLink size={14} aria-hidden="true" />
                        </AsyncButton>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        ) : null}
      </Card>

      <SlideOverPanel
        open={registrando}
        title="Registrar nota do cliente"
        onClose={() => setRegistrando(false)}
      >
        <InfoMsg style={{ marginBottom: 12 }}>
          Use quando o cliente deu a nota por telefone, no WhatsApp ou na obra —
          o link cobre quem lê e-mail, e nem todo mundo lê.
        </InfoMsg>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <ClienteSelect
            request={request}
            value={novo.cliente}
            onChange={(c) => setNovo((n) => ({ ...n, cliente: c }))}
          />
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Nota (0 a 10)</span>
            <Select
              value={novo.nota}
              onChange={(e) => setNovo((n) => ({ ...n, nota: e.target.value }))}
            >
              <option value="">— escolha —</option>
              {Array.from({ length: 11 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>O que ele disse</span>
            <Textarea
              rows={4}
              value={novo.comentario}
              onChange={(e) => setNovo((n) => ({ ...n, comentario: e.target.value }))}
            />
          </label>
          <AsyncButton
            variant="primary"
            onClick={registrar}
            successMessage="Nota registrada."
          >
            Registrar
          </AsyncButton>
        </div>
      </SlideOverPanel>
    </PageContainer>
  );
}
